import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { addDays, addMonths, addYears, eachDayOfInterval, endOfDay, endOfYear, format, isSameDay, isWithinInterval, parseISO, startOfDay, startOfYear } from 'date-fns';
import { CalendarErrorBanner } from '@/components/calendar/shared/CalendarErrorBanner';
import { CalendarCreateModal } from '@/components/calendar/modals/CalendarCreateModal';
import { PmsPanel } from '@/components/pms/PmsPanel';
import { CalendarLeftRail, type RailModuleSection } from '@/components/calendar/layout/CalendarLeftRail';
import { CalendarItemPopup } from '@/components/calendar/modals/CalendarItemPopup';
import { DayItemsPanel } from '@/components/calendar/panels/DayItemsPanel';
import { OrgOverviewPanel } from '@/components/calendar/panels/OrgOverviewPanel';
import { OrgHolidaysPanel } from '@/components/calendar/panels/OrgHolidaysPanel';
import { type CalendarVisualItem } from '@/components/calendar/views/WeekTimeGrid';
import { CalendarMainHeader } from '@/components/calendar/layout/CalendarMainHeader';
import { CalendarAgendaAside } from '@/components/calendar/layout/CalendarAgendaAside';
import { CalendarViewport } from '@/components/calendar/layout/CalendarViewport';
import {
  buildDayAgendaItems,
  buildHolidaysByDay,
  buildOrgEventsByDay,
  extractScheduleSlots,
  getDayNotes,
  getDayTasks,
  buildNotesByDay,
  buildTasksByDay,
  type DayAgendaItem,
} from '@/lib/calendarAgenda';
import { useCalendarsQuery, useDeleteNoteMutation, useEventsQuery, useMeetingsQuery, useNotesQuery, useTasksQuery } from '@/hooks/calendar';
import { useOrgHolidaysQuery, useOrgScheduleQuery, useOrgWideEventsQuery } from '@/hooks/calendar/useOrgQueries';
import { useHrmsAttendanceCurrentStatusQuery, useHrmsHolidaysQuery } from '@/hooks/hrms';
import { usePmsCalendarItems } from '@/hooks/pms/usePmsCalendarItems';
import { getActiveDays, getMiniMonth, getViewRange, type ViewKind } from '@/lib/weekCalendar';
import { buildCalendarPath, parseCalendarParams } from '@/lib/calendarRoute';
import { useWheelNavigation } from '@/hooks/calendar/useWheelNavigation';
import { cn } from '@/lib/cn';
import { getMonthGrid } from '@/lib/calendarMonth';
import { useSessionUser } from '@/hooks/auth/useSessionUser';
import { useCalendarPushRegistration } from '@/hooks/push/useCalendarPushRegistration';
import { useCalendarVisibilityPreferences } from '@/hooks/preferences/useCalendarVisibilityPreferences';
import type { OrgHoliday } from '@/api/org';
import { hrmsApi } from '@/api/hrms';

export function CalendarPage() {
  const { accessToken } = useSessionUser();
  useCalendarPushRegistration(accessToken);

  const queryClient = useQueryClient();

  // --- URL is the source of truth for view + anchor date (Google-style links) ---
  const navigate = useNavigate();
  const routeParams = useParams();
  const parsedRoute = parseCalendarParams(routeParams);
  const fallbackMs = useMemo(() => Date.now(), []);

  const view: ViewKind = parsedRoute?.view ?? 'month';
  // Memoize by epoch ms so anchorDate keeps a stable reference between renders.
  const anchorMs = parsedRoute ? parsedRoute.date.getTime() : fallbackMs;
  const anchorDate = useMemo(() => new Date(anchorMs), [anchorMs]);

  // Self-heal malformed / partial URLs into a canonical "today" deep link.
  useEffect(() => {
    if (!parsedRoute) navigate(buildCalendarPath('month', new Date()), { replace: true });
  }, [parsedRoute, navigate]);

  const goTo = useCallback(
    (nextView: ViewKind, nextDate: Date, opts?: { replace?: boolean }) => {
      navigate(buildCalendarPath(nextView, nextDate), { replace: opts?.replace });
    },
    [navigate],
  );
  const setView = useCallback((next: ViewKind) => goTo(next, anchorDate), [goTo, anchorDate]);
  const setAnchorDate = useCallback((next: Date) => goTo(view, next), [goTo, view]);

  const viewportRef = useRef<HTMLDivElement | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  // No day is pre-selected; the agenda panel stays hidden until a day is clicked.
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [railMonthAnchor, setRailMonthAnchor] = useState(() => new Date(anchorMs));
  const [createOpen, setCreateOpen] = useState(false);
  const [createDate, setCreateDate] = useState<Date | null>(() => new Date());
  const [activeItemKey, setActiveItemKey] = useState<{ id: string; kind: DayAgendaItem['kind'] } | null>(null);
  const [activeModule, setActiveModule] = useState<RailModuleSection | null>(null);
  const [isRailCollapsed, setIsRailCollapsed] = useState(false);
  const [isRailOpenMobile, setIsRailOpenMobile] = useState(false);
  const [visibilitySettingsOpen, setVisibilitySettingsOpen] = useState(false);
  const [isAgendaCollapsed, setIsAgendaCollapsed] = useState(true);

  const monthCells = useMemo(() => (view === 'month' ? getMonthGrid(anchorDate) : []), [anchorDate, view]);
  const activeDays = useMemo(() => {
    if (view === 'month' || view === 'year') return [];
    return getActiveDays(anchorDate, view);
  }, [anchorDate, view]);

  // Days we need to render day-based items (org events, holidays, schedule slots).
  // Week/day views use `activeDays`, while month/year views use their own day collections.
  const daysForItems = useMemo(() => {
    if (view === 'month') return monthCells.map((c) => c.date);
    if (view === 'year') {
      const year = anchorDate.getFullYear();
      const uniqueByKey = new Map<string, Date>();

      for (let month = 0; month < 12; month += 1) {
        const monthAnchor = new Date(year, month, 1);
        for (const cell of getMiniMonth(monthAnchor)) {
          const key = format(cell.date, 'yyyy-MM-dd');
          if (!uniqueByKey.has(key)) uniqueByKey.set(key, cell.date);
        }
      }

      return Array.from(uniqueByKey.values());
    }
    return activeDays.map((d) => d.date);
  }, [anchorDate, activeDays, monthCells, view]);
  const viewRange = useMemo(() => {
    if (view === 'month') {
      if (monthCells.length === 0) return { start: startOfDay(anchorDate), end: endOfDay(anchorDate) };
      const start = startOfDay(monthCells[0].date);
      const end = endOfDay(monthCells[monthCells.length - 1].date);
      return { start, end };
    }
    if (view === 'year') {
      return { start: startOfDay(startOfYear(anchorDate)), end: endOfYear(anchorDate) };
    }
    return getViewRange(activeDays);
  }, [activeDays, anchorDate, monthCells, view]);
  const from = viewRange.start.toISOString();
  const to = viewRange.end.toISOString();
  const visibilityQuery = useCalendarVisibilityPreferences();
  const visibility = visibilityQuery.data;
  const visibilityResolved = visibilityQuery.isSuccess || visibilityQuery.isError;
  const showEvents = visibility?.showEvents ?? true;
  const showTasks = visibility?.showTasks ?? true;
  const showPmsProjects = visibility?.showPmsProjects ?? true;
  const showNotes = visibility?.showNotes ?? true;
  const showMeetings = visibility?.showMeetings ?? true;
  const showHolidays = visibility?.showHolidays ?? true;
  const showOrgEvents = visibility?.showOrgEvents ?? true;
  const showAttendance = (visibility as { showAttendance?: boolean } | null | undefined)?.showAttendance ?? false;

  // Temporary comparator switch while HRMS is integrated as the holidays source-of-truth.
  const useCalendarOrgHolidays = String(import.meta.env.VITE_USE_CALENDAR_ORG_HOLIDAYS || '').trim() === '1';

  const deleteNote = useDeleteNoteMutation();

  const calendarsQuery = useCalendarsQuery();
  const eventsQuery = useEventsQuery({
    from,
    to,
  }, { enabled: visibilityResolved && showEvents });
  const tasksQuery = useTasksQuery({
    dueAfter: from,
    dueBefore: to,
  }, { enabled: visibilityResolved && showTasks });
  const notesQuery = useNotesQuery({
    from,
    to,
  }, { enabled: visibilityResolved && showNotes });
  const meetingsQuery = useMeetingsQuery({ from, to }, { enabled: visibilityResolved && showMeetings });

  const calendarOrgHolidaysQuery = useOrgHolidaysQuery(visibilityResolved && showHolidays && useCalendarOrgHolidays);
  const hrmsHolidaysQuery = useHrmsHolidaysQuery(visibilityResolved && showHolidays);
  const orgWideEventsQuery = useOrgWideEventsQuery(visibilityResolved && showOrgEvents, { from, to });
  const orgScheduleQuery = useOrgScheduleQuery();

  const attendanceCurrentStatusQuery = useHrmsAttendanceCurrentStatusQuery(visibilityResolved && showAttendance);

  const attendanceMonths = useMemo(() => {
    if (!showAttendance) return [];
    const pairs: Array<{ year: number; month: number }> = [];
    const start = new Date(viewRange.start);
    start.setDate(1);
    start.setHours(0, 0, 0, 0);
    const end = new Date(viewRange.end);
    end.setDate(1);
    end.setHours(0, 0, 0, 0);

    let cursor = start;
    while (cursor.getTime() <= end.getTime()) {
      pairs.push({ year: cursor.getFullYear(), month: cursor.getMonth() + 1 });
      cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
      if (pairs.length > 24) break;
    }
    return pairs;
  }, [showAttendance, viewRange.end, viewRange.start]);

  const attendanceRangeQuery = useQuery({
    queryKey: [
      'hrms',
      'attendance',
      'range',
      ...attendanceMonths.map(({ year, month }) => `${year}-${String(month).padStart(2, '0')}`),
    ],
    queryFn: async () => {
      const months = attendanceMonths;
      if (months.length === 0) return [];
      // Avoid spamming the HRMS backend with many parallel calls (e.g. Year view).
      if (months.length >= 6) {
        const all = [];
        for (const m of months) {
          all.push(...(await hrmsApi.getMonthlyAttendanceReport(m.month, m.year)));
        }
        return all;
      }
      const results = await Promise.all(months.map((m) => hrmsApi.getMonthlyAttendanceReport(m.month, m.year)));
      return results.flat();
    },
    enabled: visibilityResolved && showAttendance,
    staleTime: 60_000,
  });

  const holidaysCombined: OrgHoliday[] = useMemo(() => {
    const list: OrgHoliday[] = [];

    // Calendar org holidays (legacy / comparator)
    if (useCalendarOrgHolidays) {
      list.push(...(calendarOrgHolidaysQuery.data ?? []));
    }

    // HRMS holidays (source-of-truth)
    for (const h of hrmsHolidaysQuery.data ?? []) {
      if (h?.isActive === false) continue;
      if (!h?.date) continue;
      list.push({
        id: `hrms-${h.id}`,
        organizationId: h.organizationId ?? 'hrms',
        name: h.name ?? 'Holiday',
        startsOn: h.date,
        endsOn: null,
        yearlyRepeat: false,
        description: null,
        reminderOffsetsMinutes: null,
        createdById: 'hrms',
        updatedById: null,
        createdAt: h.date,
        updatedAt: h.date,
      });
    }

    return list;
  }, [calendarOrgHolidaysQuery.data, hrmsHolidaysQuery.data, useCalendarOrgHolidays]);

  const holidaysByDay = useMemo(() => {
    return buildHolidaysByDay(holidaysCombined, viewRange);
  }, [holidaysCombined, viewRange]);

  const attendanceByDay = useMemo(() => {
    if (!showAttendance) return {};

    const map: Record<string, 'present' | 'absent' | 'weekoff' | 'holiday'> = {};
    const todayKey = format(new Date(), 'yyyy-MM-dd');

    // Only build attendance data for past days and today — never for future dates.
    const pastAndToday = eachDayOfInterval(viewRange).filter(
      (day) => format(day, 'yyyy-MM-dd') <= todayKey
    );

    // 1) Default every past/today day to Absent (HRMS monthly report omits missing days).
    for (const day of pastAndToday) {
      const key = format(day, 'yyyy-MM-dd');
      map[key] = 'absent';
    }

    // 2) Mark weekoff days (no working schedule slots) — only for past/today.
    for (const day of pastAndToday) {
      const slots = extractScheduleSlots(orgScheduleQuery.data?.weeklyHours ?? null, day);
      if (slots.length === 0) {
        const key = format(day, 'yyyy-MM-dd');
        map[key] = 'weekoff';
      }
    }

    // 3) Overlay actual HRMS attendance records.
    for (const rec of attendanceRangeQuery.data ?? []) {
      if (!rec?.date) continue;
      const dayKey = format(parseISO(rec.date), 'yyyy-MM-dd');
      // Skip future dates even if the backend returns them.
      if (dayKey > todayKey) continue;
      const status = String(rec.status ?? '').toLowerCase();
      map[dayKey] = status === 'present' ? 'present' : status === 'holiday' ? 'holiday' : 'absent';
    }

    // 4) Overlay holidays — highest priority, only for past/today.
    for (const [dayKey, holidays] of holidaysByDay.entries()) {
      if (dayKey > todayKey) continue;
      if (holidays.length > 0) map[dayKey] = 'holiday';
    }

    return map;
  }, [
    attendanceRangeQuery.data,
    holidaysByDay,
    orgScheduleQuery.data?.weeklyHours,
    showAttendance,
    viewRange,
  ]);

  const orgEventsByDay = useMemo(() => {
    return buildOrgEventsByDay(orgWideEventsQuery.data ?? [], viewRange);
  }, [orgWideEventsQuery.data, viewRange]);

  const notesByDay = useMemo(() => buildNotesByDay(notesQuery.data ?? []), [notesQuery.data]);
  const tasksByDay = useMemo(() => buildTasksByDay(tasksQuery.data ?? []), [tasksQuery.data]);
  const meetingsInRange = useMemo(() => {
    const list = meetingsQuery.data ?? [];
    return list.filter((meeting) => {
      const startsAt = getMeetingStartsAt(meeting);
      if (!startsAt) return false;
      const date = parseISO(startsAt);
      if (Number.isNaN(date.getTime())) return false;
      return isWithinInterval(date, viewRange);
    });
  }, [meetingsQuery.data, viewRange]);

  const visualItems = useMemo(() => {
    const list: CalendarVisualItem[] = [];

    if (showEvents) {
      for (const event of eventsQuery.data ?? []) {
      const dateKey = format(parseISO(event.startsAt), 'yyyy-MM-dd');
      list.push({
        id: `event-${event.id}`,
        agendaId: event.id,
        kind: 'event',
        title: event.title,
        startsAt: event.startsAt,
        endsAt: event.endsAt,
        allDay: event.allDay,
        dateKey,
      });
    }
    }

    if (showMeetings) {
      for (const meeting of meetingsInRange) {
      const startsAt = getMeetingStartsAt(meeting);
      if (!startsAt) continue;
      const startDate = parseISO(startsAt);
      if (Number.isNaN(startDate.getTime())) continue;
      const endsAt = getMeetingEndsAt(meeting, startsAt);
      list.push({
        id: `meeting-${meeting.roomId}`,
        agendaId: meeting.roomId,
        kind: 'meeting',
        title: meeting.name || 'Untitled meeting',
        startsAt,
        endsAt,
        allDay: false,
        dateKey: format(startDate, 'yyyy-MM-dd'),
      });
    }
    }

    if (showTasks) {
      for (const task of tasksQuery.data ?? []) {
      if (!task.dueAt) continue;
      const due = parseISO(task.dueAt);
      list.push({
        id: `task-${task.id}`,
        agendaId: task.id,
        kind: 'task',
        title: task.title,
        startsAt: task.dueAt,
        endsAt: new Date(due.getTime() + 30 * 60 * 1000).toISOString(),
        allDay: false,
        dateKey: format(due, 'yyyy-MM-dd'),
      });
    }
    }

    if (showNotes) {
      for (const note of notesQuery.data ?? []) {
      if (!note.happensAt) continue;
      const at = parseISO(note.happensAt);
      list.push({
        id: `note-${note.id}`,
        agendaId: note.id,
        kind: 'note',
        title: note.title ?? 'Note',
        startsAt: note.happensAt,
        endsAt: new Date(at.getTime() + 30 * 60 * 1000).toISOString(),
        allDay: false,
        dateKey: format(at, 'yyyy-MM-dd'),
      });
    }
    }

    for (const dayDate of daysForItems) {
      const dayKey = format(dayDate, 'yyyy-MM-dd');

      if (showOrgEvents) {
        for (const orgEvent of orgEventsByDay.get(dayKey) ?? []) {
          list.push({
            id: `org-event-${orgEvent.id}-${dayKey}`,
            agendaId: orgEvent.id,
            kind: 'org-event',
            title: orgEvent.title,
            startsAt: orgEvent.startsAt,
            endsAt: orgEvent.endsAt,
            allDay: orgEvent.allDay,
            dateKey: dayKey,
          });
        }
      }

      if (showHolidays) {
        for (const holiday of holidaysByDay.get(dayKey) ?? []) {
          const startsAt = new Date(dayDate);
          startsAt.setHours(0, 0, 0, 0);
          const endsAt = new Date(dayDate);
          endsAt.setHours(23, 59, 59, 999);
          list.push({
            id: `holiday-${holiday.id}-${dayKey}`,
            agendaId: holiday.id,
            kind: 'holiday',
            title: holiday.name,
            startsAt: startsAt.toISOString(),
            endsAt: endsAt.toISOString(),
            allDay: true,
            dateKey: dayKey,
          });
        }
      }

      const scheduleSlots = extractScheduleSlots(orgScheduleQuery.data?.weeklyHours ?? null, dayDate);
      for (const [index, slot] of scheduleSlots.entries()) {
        const start = new Date(dayDate);
        start.setHours(Math.floor(slot.minutes / 60), slot.minutes % 60, 0, 0);
        const end = new Date(start.getTime() + 60 * 60 * 1000);
        list.push({
          id: `schedule-${dayKey}-${index}`,
          agendaId: `schedule-${dayKey}-${index}`,
          kind: 'schedule',
          title: slot.title,
          startsAt: start.toISOString(),
          endsAt: end.toISOString(),
          allDay: false,
          dateKey: dayKey,
        });
      }
    }

    return list;
  }, [
    eventsQuery.data,
    holidaysByDay,
    notesQuery.data,
    orgEventsByDay,
    orgScheduleQuery.data?.weeklyHours,
    showEvents,
    showHolidays,
    showMeetings,
    showNotes,
    showOrgEvents,
    showTasks,
    tasksQuery.data,
    meetingsInRange,
    daysForItems,
  ]);

  const pmsItems = usePmsCalendarItems(viewRange, {
    enabled: showTasks || showPmsProjects,
    includeTasks: showTasks,
    includeProjects: showPmsProjects,
  });
  const visiblePmsItems = useMemo(
    () =>
      pmsItems.filter(
        (item) =>
          (showTasks || item.kind !== 'pms-task') &&
          (showPmsProjects || item.kind !== 'pms-project')
      ),
    [pmsItems, showPmsProjects, showTasks]
  );
  const allVisualItems = useMemo<CalendarVisualItem[]>(
    () => [...visualItems, ...visiblePmsItems],
    [visualItems, visiblePmsItems]
  );

  const selectedAgenda = useMemo(() => {
    if (!selectedDate) return null;
    const selectedKey = format(selectedDate, 'yyyy-MM-dd');
    const dayEvents = showEvents
      ? (eventsQuery.data ?? []).filter((ev) => isSameDay(parseISO(ev.startsAt), selectedDate))
      : [];
    const dayMeetings = showMeetings
      ? meetingsInRange.filter((meeting) => {
          const startsAt = getMeetingStartsAt(meeting);
          if (!startsAt) return false;
          const startDate = parseISO(startsAt);
          if (Number.isNaN(startDate.getTime())) return false;
          return isSameDay(startDate, selectedDate);
        })
      : [];
    const dayOrgEvents = showOrgEvents ? orgEventsByDay.get(selectedKey) ?? [] : [];
    const dayHolidays = showHolidays ? holidaysByDay.get(selectedKey) ?? [] : [];
    const dayNotes = showNotes ? getDayNotes(selectedDate, notesByDay) : [];
    const dayTasks = showTasks ? getDayTasks(selectedDate, tasksByDay) : [];
    const calendarItems = buildDayAgendaItems({
      date: selectedDate,
      dayEvents,
      dayMeetings,
      dayOrgEvents,
      dayHolidays,
      dayNotes,
      dayTasks,
      weeklyHours: orgScheduleQuery.data?.weeklyHours ?? null,
    });

    const pmsDayItems: DayAgendaItem[] = visiblePmsItems
      .filter((item) => item.dateKey === selectedKey)
      .map((item) => ({
        id: item.agendaId ?? item.id,
        kind: item.kind,
        title: item.title,
        allDay: item.allDay,
        startsAt: item.startsAt,
        endsAt: item.endsAt,
        timeLabel: item.allDay ? 'All day' : format(parseISO(item.startsAt), 'p'),
        details: null,
      }));

    return [...calendarItems, ...pmsDayItems];
  }, [
    eventsQuery.data,
    holidaysByDay,
    meetingsInRange,
    notesByDay,
    orgEventsByDay,
    orgScheduleQuery.data?.weeklyHours,
    visiblePmsItems,
    selectedDate,
    showEvents,
    showHolidays,
    showMeetings,
    showNotes,
    showOrgEvents,
    showTasks,
    tasksByDay,
  ]);

  const popupItem: DayAgendaItem | null = useMemo(() => {
    if (!selectedAgenda || !activeItemKey) return null;
    return selectedAgenda.find((i) => i.id === activeItemKey.id && i.kind === activeItemKey.kind) ?? null;
  }, [activeItemKey, selectedAgenda]);

  const openItemPopup = (date: Date, key: { id: string; kind: DayAgendaItem['kind'] }) => {
    setSelectedDate(date);
    setActiveItemKey(key);
  };

  const handleDateSelection = (date: Date) => {
    setSelectedDate(date);
    setAnchorDate(date);
    setRailMonthAnchor(date);
    setIsAgendaCollapsed(false);
  };

  const openModulePanel = (section: RailModuleSection) => {
    setActiveModule(section);
    setActiveItemKey(null);
  };

  const closeModulePanel = () => setActiveModule(null);

  const shiftRailYear = (dir: -1 | 1) => {
    setRailMonthAnchor((d) => addYears(d, dir));
    setAnchorDate(addYears(anchorDate, dir));
  };

  const setRailYear = (year: number) => {
    const month = railMonthAnchor.getMonth();
    const day = railMonthAnchor.getDate();
    const maxDay = new Date(year, month + 1, 0).getDate();
    const next = new Date(year, month, Math.min(day, maxDay));
    setRailMonthAnchor(next);
    setAnchorDate(next);
  };

  const railYear = railMonthAnchor.getFullYear();
  const yearRange = useMemo(() => {
    const start = startOfYear(new Date(railYear, 0, 1));
    const end = endOfYear(new Date(railYear, 0, 1));
    return {
      from: start.toISOString(),
      to: end.toISOString(),
    };
  }, [railYear]);

  const orgRange = useMemo(() => {
    return {
      from: yearRange.from,
      to: yearRange.to,
    };
  }, [yearRange.from, yearRange.to]);

  const yearEventsQuery = useEventsQuery({
    from: activeModule === 'events' ? yearRange.from : '',
    to: activeModule === 'events' ? yearRange.to : '',
  });

  const yearTasksQuery = useTasksQuery(
    { dueAfter: yearRange.from, dueBefore: yearRange.to },
    { enabled: activeModule === 'tasks' }
  );

  const yearNotesQuery = useNotesQuery(
    { from: yearRange.from, to: yearRange.to },
    { enabled: activeModule === 'notes' }
  );

  const refreshAll = () => {
    void queryClient.invalidateQueries({ queryKey: ['calendars'] });
    void queryClient.invalidateQueries({ queryKey: ['events'] });
    void queryClient.invalidateQueries({ queryKey: ['tasks'] });
    void queryClient.invalidateQueries({ queryKey: ['notes'] });
    void queryClient.invalidateQueries({ queryKey: ['meetings'] });
    void queryClient.invalidateQueries({ queryKey: ['org', 'holidays'] });
    void queryClient.invalidateQueries({ queryKey: ['hrms', 'holidays'] });
    void queryClient.invalidateQueries({ queryKey: ['org', 'wide-events'] });
    void queryClient.invalidateQueries({ queryKey: ['org', 'schedule'] });
  };

  const loadError =
    (showEvents ? eventsQuery.error : null) ??
    (showTasks ? tasksQuery.error : null) ??
    (showNotes ? notesQuery.error : null) ??
    (showMeetings ? meetingsQuery.error : null) ??
    calendarsQuery.error ??
    (showHolidays ? hrmsHolidaysQuery.error : null) ??
    (showOrgEvents ? orgWideEventsQuery.error : null) ??
    orgScheduleQuery.error;

  const stepAnchor = (dir: -1 | 1) => {
    let next: Date;
    if (view === 'month') next = addMonths(anchorDate, dir);
    else if (view === 'year') next = addYears(anchorDate, dir);
    else if (view === 'day') next = addDays(anchorDate, dir);
    else if (view === '2day') next = addDays(anchorDate, dir * 2);
    else if (view === '4day') next = addDays(anchorDate, dir * 4);
    else next = addDays(anchorDate, dir * 7);
    
    setAnchorDate(next);
    setRailMonthAnchor(next);
  };

  // Keep the mini-month rail in sync with the anchor (covers browser back/forward,
  // where no click handler runs but the URL — and thus the anchor — changes).
  useEffect(() => {
    setRailMonthAnchor(new Date(anchorMs));
  }, [anchorMs]);

  // Google-style scroll-to-navigate: month view responds to both axes (vertical
  // gated on scroll boundary); week/day/year navigate on horizontal gestures only
  // so vertical scrolling of the time grid / overview stays native.
  useWheelNavigation(viewportRef, {
    enabled: !createOpen && !activeModule,
    axis: view === 'month' ? 'both' : 'horizontal',
    scrollContainer: scrollContainerRef,
    onPrev: () => stepAnchor(-1),
    onNext: () => stepAnchor(1),
  });

  const showAgendaAside = !activeModule || activeModule === 'pms';
  const compactYearLayout = view === 'year' && showAgendaAside && !isAgendaCollapsed;

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-background lg:flex-row">
      {/* Sidebar / Left Rail */}
      <CalendarLeftRail
        miniMonth={getMiniMonth(railMonthAnchor)}
        monthAnchor={railMonthAnchor}
        selectedDate={selectedDate}
        collapsed={isRailCollapsed}
        mobileOpen={isRailOpenMobile}
        onCreate={() => {
          setCreateDate(selectedDate ?? new Date());
          setCreateOpen(true);
          setIsRailOpenMobile(false);
        }}
        onToggleCollapse={() => setIsRailCollapsed((v) => !v)}
        onCloseMobile={() => setIsRailOpenMobile(false)}
        onPrevMonth={() => setRailMonthAnchor((d) => addMonths(d, -1))}
        onNextMonth={() => setRailMonthAnchor((d) => addMonths(d, 1))}
        onSelectDate={(d) => {
          handleDateSelection(d);
          setIsRailOpenMobile(false);
        }}
        onPrevYear={() => shiftRailYear(-1)}
        onNextYear={() => shiftRailYear(1)}
        onSelectYear={setRailYear}
        activeSection={activeModule}
        onOpenModule={openModulePanel}
      />

      {/* Main Content Area */}
      <main className="relative flex flex-1 flex-col overflow-hidden">
        <CalendarMainHeader
          anchorDate={anchorDate}
          view={view}
          isRailCollapsed={isRailCollapsed}
          isAgendaCollapsed={isAgendaCollapsed}
          visibilitySettingsOpen={visibilitySettingsOpen}
          onOpenMobileRail={() => setIsRailOpenMobile(true)}
          onToggleRail={() => setIsRailCollapsed((v) => !v)}
          onShowAgenda={() => setIsAgendaCollapsed(false)}
          onToday={() => {
            const now = new Date();
            setAnchorDate(now);
            setSelectedDate(now);
            setRailMonthAnchor(now);
          }}
          onStep={stepAnchor}
          onSetView={setView}
          onRefresh={refreshAll}
          onToggleVisibilitySettings={() => setVisibilitySettingsOpen((v) => !v)}
        />

        <div ref={scrollContainerRef} className="custom-scrollbar flex-1 overflow-y-auto bg-[#F6F7FB] bg-[radial-gradient(ellipse_70%_60%_at_100%_0%,rgba(129,140,248,0.28),transparent_70%)] dark:bg-background dark:bg-[radial-gradient(ellipse_70%_60%_at_100%_0%,rgba(139,124,255,0.18),transparent_70%)] px-4 py-4 sm:px-6 sm:py-5 lg:px-8 lg:py-6">
          <CalendarErrorBanner error={loadError} label="Could not load calendar data" />

          <div className="flex min-h-full flex-col xl:flex-row">
            <div ref={viewportRef} className="min-w-0 flex-1">
              <CalendarViewport
                view={view}
                anchorDate={anchorDate}
                selectedDate={selectedDate}
                monthCells={monthCells}
                activeDays={activeDays}
                allVisualItems={allVisualItems}
                attendanceByDay={showAttendance ? attendanceByDay : null}
                compactYearLayout={compactYearLayout}
                onSelectDate={handleDateSelection}
                onSelectMonth={(monthAnchor) => {
                  goTo('month', monthAnchor);
                  setSelectedDate(monthAnchor);
                  setRailMonthAnchor(monthAnchor);
                }}
                onOpenItemPopup={openItemPopup}
              />
            </div>

            {showAgendaAside ? (
              <div
                aria-hidden={isAgendaCollapsed}
                className={cn(
                  // Cross-axis collapse: width on desktop (xl row layout),
                  // height on stacked layout — both animated smoothly.
                  'grid shrink-0 overflow-hidden transition-[grid-template-rows,width,margin,opacity] duration-300 ease-in-out',
                  isAgendaCollapsed
                    ? 'mt-0 grid-rows-[0fr] opacity-0 xl:ml-0 xl:w-0 xl:grid-rows-[1fr]'
                    : 'mt-5 grid-rows-[1fr] opacity-100 xl:ml-8 xl:mt-0 xl:w-[300px] 2xl:w-[360px]',
                )}
              >
                <div className="min-h-0 overflow-hidden xl:w-[300px] 2xl:w-[360px]">
                  <CalendarAgendaAside
                    selectedDate={selectedDate}
                    selectedAgenda={selectedAgenda}
                    attendanceToday={showAttendance ? (attendanceCurrentStatusQuery.data ?? null) : null}
                    attendanceByDay={showAttendance ? attendanceByDay : null}
                    onHide={() => setIsAgendaCollapsed(true)}
                    onOpenItem={(item) => {
                      if (!selectedDate) return;
                      openItemPopup(selectedDate, { id: item.id, kind: item.kind });
                    }}
                  />
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </main>

      {/* Panels & Modals */}
      {activeModule === 'events' || activeModule === 'tasks' || activeModule === 'notes' ? (
        <DayItemsPanel
          isOpen
          onClose={closeModulePanel}
          section={activeModule === 'events' ? 'events' : activeModule === 'tasks' ? 'tasks' : 'notes'}
          selected={selectedDate}
          mode="range"
          rangeLabel={String(railYear)}
          dayEvents={yearEventsQuery.data ?? []}
          tasks={yearTasksQuery.data ?? []}
          notes={yearNotesQuery.data ?? []}
          tasksLoading={yearTasksQuery.isPending}
          notesLoading={yearNotesQuery.isPending}
        />
      ) : null}

      {activeModule === 'org-events' ? (
        <OrgOverviewPanel
          isOpen
          onClose={closeModulePanel}
          initialSection="events"
          range={orgRange}
        />
      ) : null}

      {activeModule === 'holidays' ? (
        <OrgHolidaysPanel isOpen onClose={closeModulePanel} />
      ) : null}

      {activeModule && activeModule !== 'pms' ? null : (
        <CalendarItemPopup
          item={popupItem}
          date={selectedDate}
          onClose={() => setActiveItemKey(null)}
          onDeleteNote={(id) => deleteNote.mutate(id, { onSuccess: () => setActiveItemKey(null) })}
          isDeletingNote={deleteNote.isPending}
        />
      )}

      <CalendarCreateModal
        isOpen={createOpen}
        selectedDate={createDate}
        onClose={() => setCreateOpen(false)}
      />

      <PmsPanel
        isOpen={activeModule === 'pms'}
        onClose={closeModulePanel}
      />
    </div>
  );
}

function getMeetingStartsAt(meeting: { scheduledAt?: string | null; createdAt?: string | null }): string | null {
  return meeting.scheduledAt ?? meeting.createdAt ?? null;
}

function getMeetingEndsAt(meeting: { duration?: number | null }, startsAt: string): string {
  const startDate = parseISO(startsAt);
  if (Number.isNaN(startDate.getTime())) return startsAt;
  const durationMinutes = Number(meeting.duration ?? 60);
  const safeDuration = Number.isFinite(durationMinutes) && durationMinutes > 0 ? durationMinutes : 60;
  return new Date(startDate.getTime() + safeDuration * 60 * 1000).toISOString();
}
